import {
  AppBar,
  Box,
  Toolbar,
  Stack,
  Typography,
  Button,
  useTheme,
  IconButton,
  Drawer,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import LogoIcon from "../../images/logo.svg";
import MenuIcon from "../../images/topbar.svg";
import LoginIcon from "../../images/login.svg";
import QueryPreservingLink from "../../components/QueryPreservingLink";
import pages from "../pages";
import Dropdown from "./Dropdown";
import Sidebar from "./Sidebar";
import { useSelector } from "react-redux";
import Profile from "./Profile";
import useHasWindow from "../../helpers/useHasWindow";
import StickyStripe from "./StickyStripe/StickyStripe";
import { trackMenuNewUser } from "../../helpers/analytics";

const Topbar = ({ recentReportId, location }) => {
  const theme = useTheme();
  const [drawer, setDrawer] = useState(false);
  const authLoader = useSelector((state) => state.base.authLoader);
  const isLoggedIn = useSelector((state) => Boolean(state.user.userId));
  const hasWindow = useHasWindow();

  return (
    <>
      <AppBar position="sticky" color="inherit" elevation={0}>
        <StickyStripe />

        <Toolbar
          sx={{
            height: "90px",
            boxShadow: "6.4px 6.4px 32px 0 rgba(154, 170, 176, 0.17)",
          }}
        >
          <QueryPreservingLink to="/">
            <Box
              component="img"
              src={LogoIcon}
              alt="logo"
              sx={{ height: "45px", width: "125px" }}
            />
          </QueryPreservingLink>

          <Stack
            direction={"row"}
            sx={{
              flexGrow: 1,
              justifyContent: "end",
              alignItems: "center",
              display: { xs: "flex", lg: "none" },
            }}
            gap={1}
          >
            {hasWindow && (
              <>
                {authLoader ? (
                  <CircularProgress />
                ) : (
                  <>
                    {!isLoggedIn && (
                      <IconButton
                        size="small"
                        component={QueryPreservingLink}
                        to="/sign_in"
                      >
                        <Box
                          component="img"
                          src={LoginIcon}
                          alt="menu-icon"
                          sx={{
                            width: 25,
                            height: 25,
                            objectFit: "contain",
                          }}
                        />
                      </IconButton>
                    )}
                  </>
                )}
              </>
            )}

            <IconButton size="small" onClick={() => setDrawer(!drawer)}>
              <Box
                component="img"
                src={MenuIcon}
                alt="menu-icon"
                sx={{
                  width: 25,
                  height: 25,
                  objectFit: "contain",
                }}
              />
            </IconButton>
          </Stack>

          <Stack
            direction="row"
            sx={{
              flexGrow: 1,
              justifyContent: "end",
              alignItems: "center",
              display: { xs: "none", lg: "flex" },
            }}
            gap={4}
          >
            {pages.map((page, index) => {
              return page.submenu ? (
                <Dropdown
                  key={index}
                  page={page}
                  recentReportId={recentReportId}
                  location={location}
                />
              ) : page.url.includes("https") ? (
                <Box
                  component="a"
                  key={index}
                  href={page.url}
                  sx={{
                    fontWeight: "bold",
                    textDecoration: "none",
                    color: theme.palette.secondary.main,
                    fontSize: "14px",
                  }}
                >
                  {page.name}
                </Box>
              ) : (
                <Typography
                  component={QueryPreservingLink}
                  key={index}
                  to={page.url}
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    textDecoration: "none",
                    color: theme.palette.secondary.main,
                    "&.active": {
                      color: theme.palette.primary.main,
                    },
                  }}
                  activeClassName="active"
                >
                  {page.name}
                </Typography>
              );
            })}

            {hasWindow && (
              <>
                {authLoader ? (
                  <CircularProgress />
                ) : (
                  <Stack direction="row" gap={2}>
                    {isLoggedIn ? (
                      <Profile />
                    ) : (
                      <>
                        <Button
                          size="small"
                          sx={{ fontSize: 14 }}
                          component={QueryPreservingLink}
                          to="/sign_in"
                        >
                          Login
                        </Button>

                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          sx={{ fontSize: 14 }}
                          component={QueryPreservingLink}
                          to="/sign_up"
                          onClick={trackMenuNewUser}
                        >
                          Sign up
                        </Button>
                      </>
                    )}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        open={drawer}
        onClose={() => setDrawer(!drawer)}
        anchor="right"
        keepMounted={true}
      >
        <Sidebar
          setDrawer={setDrawer}
          recentReportId={recentReportId}
          location={location}
        />
      </Drawer>
    </>
  );
};

export default Topbar;
